import { TraversalPathMatchingType } from '@ardoq/api-types';
import { anyType } from '@ardoq/global-consts';
import { TraversalBuilderState } from '../types';
import { createStartNode } from './createStartNode';
import {
  SetStartTypeAndTraversalStartSetOrCount,
  UpdateTraversalStartContextPayload,
} from './editTraversalActions';
import { pathToKey } from './pathToKey';
import { setTriplesOptions } from './setTraversal';
import { withoutStartSetExpansion } from './withoutStartSetExpansion';

/**
 *
 * This operation clears the graph if the provided start type is different to
 * the current one.
 */
export const setStartTypeAndTraversalStartSetOrCount = (
  state: TraversalBuilderState,
  {
    startType,
    traversalStartSet = [],
    traversalStartQuery = null,
    traversalStartQueryResultCount,
  }: SetStartTypeAndTraversalStartSetOrCount
): TraversalBuilderState => {
  const { rawGraphNodes, isMetaModelLoaded } = state;
  if (!isMetaModelLoaded) {
    return {
      ...state,
      startComponentType: startType,
      traversalStartQuery,
      traversalStartSet,
    };
  }

  if (
    startType &&
    startType === state.startComponentType &&
    state.startNode &&
    traversalStartSet
  ) {
    const newState = { ...state, traversalStartSet, traversalStartQuery };
    // When opening the viewpoint builder the options are potentially set
    // before the start set or query are set. If that happens we should make
    // sure here that we request the instance counts (which `setTriplesOptions`
    // will take care of).
    if (!state.tripleOptions.fetchCountsArgs) {
      return setTriplesOptions(newState);
    }
    return newState;
  }

  const { startNode, counts } = createStartNode({
    startType,
    traversalStartSet,
    traversalStartQueryResultCount,
    rawGraphNodes,
    isHierarchyWildcardActive: Boolean(state.startSetExpansionState),
  });
  if (!startNode) return state;

  const instanceCounts: TraversalBuilderState['instanceCounts'] = new Map([
    [
      startNode.id,
      {
        namedDirectedTriplePath: [],
        graphNodeId: startNode.id,
        pathKey: pathToKey([]),
        label: startNode.getLabel(),
        counts,
      },
    ],
  ]);

  const selectedGraph = {
    sourceMap: new Map([[startNode.id, []]]),
    targetMap: new Map(),
    referenceMap: new Map(),
  };
  const graphNodes = new Map([[startNode.id, startNode]]);
  const graphEdges = new Map();

  return setTriplesOptions({
    ...state,
    startComponentType: startType,
    startComponentTypeId: startNode.modelId,
    selectedGraph,
    startNode,
    graphNodes,
    graphEdges,
    selectedGraphNodeId: startNode.id,
    traversalStartSet,
    traversalStartQuery,
    instanceCounts,
    requiredNodeIds: [],
    pathMatching: TraversalPathMatchingType.LOOSE,
    pathCollapsing: {
      rules: [],
      currentEditableRule: null,
    },
    highlightedPaths: {},
    wildcardState: null,
  });
};

export const updateTraversalStartContext = (
  state: TraversalBuilderState,
  {
    startComponentType,
    traversalStartQuery,
    traversalStartSet,
    selectedStartComponentTypeNames,
  }: UpdateTraversalStartContextPayload
): TraversalBuilderState => {
  // The starting type is determined by the components selected by the user.
  // Therefore, we should avoid resetting the graph based on this.
  // There could be instances where the user modifies the selection, resulting
  // in a temporary state where no components are selected.
  if (!startComponentType) {
    return state;
  }

  // Under an expansion `startComponentType` is the wildcard sentinel, so compare
  // against the type the expansion started from - otherwise picking another
  // component of the same type reads as a type change and clears the graph. The
  // sentinel itself also arrives whenever the start context has no concrete type
  // to report, which is not a type change either.
  const isCurrentStartComponentType = state.startSetExpansionState
    ? startComponentType ===
        state.startSetExpansionState.originalStartComponentType ||
      startComponentType === anyType
    : startComponentType === state.startComponentType;

  // An expansion stands for one hierarchy, so it survives neither a different
  // start type nor a start context spanning several types. Left in place it keeps
  // a node for a hierarchy the start set no longer matches, and puts its own type
  // back when the checkbox is unticked.
  const shouldDropStartSetExpansion =
    Boolean(state.startSetExpansion) &&
    (!isCurrentStartComponentType ||
      selectedStartComponentTypeNames.length > 1);

  if (isCurrentStartComponentType && !shouldDropStartSetExpansion) {
    return setTriplesOptions({
      ...state,
      traversalStartQuery,
      traversalStartSet,
    });
  }

  // Either a different component type or a start context spanning several: the
  // graph is cleared, and any expansion goes with the hierarchy it was defined
  // for.
  const stateToSwitch = shouldDropStartSetExpansion
    ? withoutStartSetExpansion(state)
    : state;
  const switchedState = setStartTypeAndTraversalStartSetOrCount(stateToSwitch, {
    startType: startComponentType,
    traversalStartQuery: traversalStartQuery,
    traversalStartSet: traversalStartSet ?? undefined,
  });

  // The switch declines when the requested type has no raw graph node, returning
  // the state it was given. Keeping the torn-down state would leave the wildcard
  // start node in place with its expansion gone, so keep the state as it was.
  if (switchedState === stateToSwitch) {
    return state;
  }
  return switchedState;
};
